// src/utils/generarRecurrentes.js
// Función reutilizable para generar movimientos recurrentes pendientes.
// Se usa desde Contabilidad.js (automático al abrir) y GastosRecurrentesModal.js (botón manual).

import supabase from "../supabaseClient";

/* ═══════════════════════════════════════════════════════════════
   Helpers de fecha LOCAL (Colombia) — NUNCA usar toISOString() que
   devuelve UTC y corre las fechas un día en las noches.
   ═══════════════════════════════════════════════════════════════ */
function fechaLocalISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dia}`;
}

// Parsea "yyyy-mm-dd" como fecha LOCAL (no UTC). new Date("2026-05-04")
// se interpreta como UTC y puede correr el día; esto lo evita.
function parseLocalDate(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/* ═══════════════════════════════════════════════════════════════
   Candado anti-concurrencia por tenant.
   Evita que la auto-generación (al abrir Contabilidad) y el botón
   manual — o un doble montaje de React StrictMode — corran a la vez
   y generen duplicados antes de que el chequeo "ya existe" alcance.
   La capa fuerte es el índice único en Supabase; esto es el cinturón.
   ═══════════════════════════════════════════════════════════════ */
const generacionEnCurso = new Set();

/* ═══════════════════════════════════════════════════════════════
   Calcular fechas pendientes de generación para un recurrente
   ═══════════════════════════════════════════════════════════════ */
function calcularFechasPendientes(rec, hastaFecha) {
  const fechas = [];
  const hoy = hastaFecha || new Date();
  const hoyStr = fechaLocalISO(hoy);

  let cursor;
  if (rec.ultimo_generado) {
    cursor = parseLocalDate(rec.ultimo_generado);
    cursor.setDate(cursor.getDate() + 1);
  } else {
    const fi = parseLocalDate(rec.fecha_inicio);
    cursor = new Date(fi.getFullYear(), fi.getMonth(), 1);
  }

  const maxIteraciones = 365;
  let iteraciones = 0;

  while (iteraciones < maxIteraciones) {
    const fechaCobro = calcularProximaFecha(cursor, rec.frecuencia, rec.dia_cobro);
    if (!fechaCobro) break;

    const fechaStr = fechaLocalISO(fechaCobro);
    if (fechaStr > hoyStr) break;
    if (rec.fecha_fin && fechaStr > rec.fecha_fin) break;

    const fechaCobroDate = parseLocalDate(fechaStr);
    const inicioDate = parseLocalDate(rec.fecha_inicio);
    const mismoMesOPosterior =
      fechaCobroDate.getFullYear() > inicioDate.getFullYear() ||
      (fechaCobroDate.getFullYear() === inicioDate.getFullYear() &&
       fechaCobroDate.getMonth() >= inicioDate.getMonth());

    if (mismoMesOPosterior) {
      fechas.push(fechaStr);
    }

    cursor = new Date(fechaCobro);
    cursor.setDate(cursor.getDate() + 1);
    iteraciones++;
  }

  return fechas;
}

function calcularProximaFecha(desde, frecuencia, diaCobro) {
  const d = new Date(desde);
  const dia = Math.min(diaCobro || 1, 28);

  switch (frecuencia) {
    case "semanal": {
      const target = new Date(d);
      target.setDate(d.getDate() + ((7 - d.getDay() + (dia % 7)) % 7 || 7));
      if (target <= d) target.setDate(target.getDate() + 7);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + (7 - 1));
    }
    case "quincenal": {
      const y = d.getFullYear(), m = d.getMonth();
      const opcion1 = new Date(y, m, dia);
      const opcion2 = new Date(y, m, Math.min(dia + 15, 28));
      if (opcion1 >= d) return opcion1;
      if (opcion2 >= d) return opcion2;
      return new Date(y, m + 1, dia);
    }
    case "mensual": {
      const y = d.getFullYear(), m = d.getMonth();
      const enEsteMes = new Date(y, m, dia);
      if (enEsteMes >= d) return enEsteMes;
      return new Date(y, m + 1, dia);
    }
    case "bimestral": {
      const y = d.getFullYear(), m = d.getMonth();
      const enEsteMes = new Date(y, m, dia);
      if (enEsteMes >= d) return enEsteMes;
      return new Date(y, m + 2, dia);
    }
    case "trimestral": {
      const y = d.getFullYear(), m = d.getMonth();
      const enEsteMes = new Date(y, m, dia);
      if (enEsteMes >= d) return enEsteMes;
      return new Date(y, m + 3, dia);
    }
    case "anual": {
      const y = d.getFullYear(), m = d.getMonth();
      const enEsteAnio = new Date(y, m, dia);
      if (enEsteAnio >= d) return enEsteAnio;
      return new Date(y + 1, m, dia);
    }
    default:
      return null;
  }
}

/* ═══════════════════════════════════════════════════════════════
   Función principal: generar movimientos pendientes
   - tenantId: ID del tenant actual (obligatorio para INSERT)
   - silent: si es true, no muestra Swal (modo automático)
   Retorna: { totalGenerados: number }
   ═══════════════════════════════════════════════════════════════ */
export async function generarMovimientosRecurrentes(tenantId, silent = false) {
  if (!tenantId) return { totalGenerados: 0 };

  // Candado: si ya hay una generación en curso para este tenant, no arrancar otra
  if (generacionEnCurso.has(tenantId)) {
    return { totalGenerados: 0, enCurso: true };
  }
  generacionEnCurso.add(tenantId);

  try {
    const hoy = new Date();
    const hoyStr = fechaLocalISO(hoy);

    // Cargar recurrentes activos
    const { data: recurrentes, error: errorCarga } = await supabase
      .from("gastos_recurrentes")
      .select("*")
      .eq("activo", true);

    if (errorCarga || !recurrentes || recurrentes.length === 0) {
      return { totalGenerados: 0 };
    }

    let totalGenerados = 0;

    for (const rec of recurrentes) {
      if (rec.fecha_fin && rec.fecha_fin < hoyStr) continue;

      const fechasAGenerar = calcularFechasPendientes(rec, hoy);
      let ultimaGeneradaOK = rec.ultimo_generado || null;

      for (const fecha of fechasAGenerar) {
        // Capa 1: verificar que no exista ya (lectura rápida)
        const { data: existe } = await supabase
          .from("movimientos_contables")
          .select("id")
          .eq("recurrente_id", rec.id)
          .eq("fecha", fecha)
          .limit(1);

        if (existe && existe.length > 0) {
          ultimaGeneradaOK = fecha; // ya está, avanzar el cursor igual
          continue;
        }

        // Capa 2: insertar. Si el índice único rebota (23505), lo tratamos
        // como "ya existía" y seguimos sin romper nada.
        const { error } = await supabase.from("movimientos_contables").insert([{
          tipo: rec.tipo,
          monto: rec.monto,
          descripcion: rec.descripcion,
          categoria: rec.categoria || "",
          fecha: fecha,
          estado: "activo",
          origen: "recurrente",
          recurrente_id: rec.id,
          orden_id: null, // explícito: evita el default gen_random_uuid() de la columna
          tenant_id: tenantId,
        }]);

        if (!error) {
          totalGenerados++;
          ultimaGeneradaOK = fecha;
        } else if (error.code === "23505") {
          // Violación de índice único = duplicado evitado por la DB. No es un fallo real.
          ultimaGeneradaOK = fecha;
        } else {
          console.error("Error insertando recurrente:", error);
        }
      }

      // Actualizar ultimo_generado a la última fecha procesada con éxito
      if (ultimaGeneradaOK && ultimaGeneradaOK !== rec.ultimo_generado) {
        await supabase.from("gastos_recurrentes")
          .update({ ultimo_generado: ultimaGeneradaOK })
          .eq("id", rec.id);
      }
    }

    return { totalGenerados };
  } catch (err) {
    console.error("Error en generarMovimientosRecurrentes:", err);
    return { totalGenerados: 0 };
  } finally {
    // Siempre liberar el candado, pase lo que pase
    generacionEnCurso.delete(tenantId);
  }
}

// Exportar también las funciones helper para que el modal las use si necesita
export { calcularFechasPendientes, calcularProximaFecha };